import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    NATIONAL_LEVEL,
    PROVINCE_LEVEL,
    getLocationsForLevel,
    getMaxLocationDetailLevel,
    updateEchartsMapVisibleLocations,
} from '@portal/common/components';
import { MAP_DETAIL_LEVEL_ID_KEY, MAP_TYPE_ID_KEY } from '@portal/common/models/data-key';
import {
    DataCollectionResource,
    DataCondition,
    DataExportFormat,
    DataFilters,
    DataGranularity,
    DataGranularityKey,
    DataResponse,
    DataType,
    TimeUnitKey,
} from '@portal/common/types';

import config from '../../../../config';
import {
    getEntityHierarchies,
    getSelectedConditionsRefinedGranularity,
} from '../../../../store/data-explorer/selectors';
import { checkIsDengueDataTool } from '../../../../store/user-settings/selectors';
import ChartRefinementFiltersControls from '../ChartRefinementFiltersControls';
import Container from './Container';

const UPDATE_MAP_ON_MOUNT_TIMEOUT = 1000;

type Props = {
    initialTimeUnitValue: number | string;
    onDataLoad?: () => void;
    conditionsDataTypes: DataType[];
    multipleFilterKeys: DataGranularityKey[];
    selectedDataTool: string;
    selectedDataCollection: DataCollectionResource;
    conditionsPrimaryEntityFilters: DataGranularityKey[];
    isLoadingChartData: boolean;
    chartFilters: DataFilters;
    filtersValues: DataFilters; // @todo: rename to 'selectedRefinementFilters', for example
    chartDataResponsesByType: null | Record<string, DataResponse>;
    conditions: DataCondition[];
    conditionsDataTypesGranularity: DataGranularity;

    timeUnitKey: TimeUnitKey;
    onExportData?: (format: DataExportFormat) => void;
    enableExportPNG: boolean;
    numberFormatter: (value: number | string) => string;

    theme?: any;
};

type State = {
    echartsMapKey: string;
    mapElementsAmount: number;
    isMapInitialized: boolean;
};

const defaultState: State = {
    echartsMapKey: 'world',
    mapElementsAmount: 0,
    isMapInitialized: false,
};

const MapChart = ({
    initialTimeUnitValue,
    conditionsDataTypes,
    conditionsDataTypesGranularity,
    selectedDataTool,
    selectedDataCollection,
    conditionsPrimaryEntityFilters,

    isLoadingChartData,
    chartDataResponsesByType,
    filtersValues,
    conditions,
    chartFilters,

    timeUnitKey,
    onExportData,
    enableExportPNG,
    numberFormatter,
    theme,
}: Props) => {
    const entityHierarchies = useSelector(getEntityHierarchies);
    const isDengueTool = useSelector(checkIsDengueDataTool);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const [state, setState] = useState<State>(defaultState);
    const chartRef = useRef(null);

    const resetChartPosition = useCallback(() => {
        if (chartRef?.current) {
            chartRef.current.resetChartPosition();
        }
    }, []);

    const updateMap = useCallback(
        (filters) => {
            const detailLevel = filters.hasOwnProperty(MAP_DETAIL_LEVEL_ID_KEY)
                ? filters[MAP_DETAIL_LEVEL_ID_KEY]
                : NATIONAL_LEVEL;
            const mapType = filters[MAP_TYPE_ID_KEY] || config.globalLocationId;
            const locationIds = getLocationsForLevel(
                detailLevel,
                mapType,
                refinedGranularity?.location_id,
                entityHierarchies.locations
            );

            const [echartsMapKey, mapElementsAmount] = updateEchartsMapVisibleLocations(
                locationIds,
                mapType
            );

            setState({ echartsMapKey, mapElementsAmount, isMapInitialized: true });
        },
        [refinedGranularity, entityHierarchies.locations]
    );

    const getDetailLevel = useCallback(
        (filters: DataFilters, prevFilters: DataFilters): number => {
            const mapType = filters[MAP_TYPE_ID_KEY];
            const prevMapType = prevFilters[MAP_TYPE_ID_KEY];
            const detailLevel = filters[MAP_DETAIL_LEVEL_ID_KEY];

            let nextDetailLevel = detailLevel;
            if (
                prevMapType === config.globalLocationId &&
                mapType !== config.globalLocationId &&
                detailLevel === NATIONAL_LEVEL
            ) {
                nextDetailLevel = PROVINCE_LEVEL;
            } else if (
                prevMapType !== config.globalLocationId &&
                mapType === config.globalLocationId &&
                detailLevel !== NATIONAL_LEVEL
            ) {
                nextDetailLevel = NATIONAL_LEVEL;
            }

            const maxAvailableLevel = getMaxLocationDetailLevel(
                mapType,
                refinedGranularity?.location_id,
                entityHierarchies.locations
            );

            return Math.min(nextDetailLevel, maxAvailableLevel);
        },
        [refinedGranularity, entityHierarchies.locations]
    );

    const preprocessFiltersChange = useCallback(
        (filters: DataFilters) => {
            if (!chartFilters) {
                updateMap(filters);
                return filters;
            }

            const combinedFilters = { ...chartFilters, ...filters };

            const isFilterChanged = (filterKey, newFilters): boolean =>
                chartFilters[filterKey] !== newFilters[filterKey];

            if (isFilterChanged(MAP_TYPE_ID_KEY, filters)) {
                combinedFilters[MAP_DETAIL_LEVEL_ID_KEY] = getDetailLevel(filters, chartFilters);
            }

            const isMapTypeChanged = isFilterChanged(MAP_TYPE_ID_KEY, combinedFilters);
            if (
                isMapTypeChanged ||
                isFilterChanged(MAP_DETAIL_LEVEL_ID_KEY, combinedFilters) ||
                !state.isMapInitialized
            ) {
                updateMap(combinedFilters);
            }

            if (isMapTypeChanged && combinedFilters[MAP_TYPE_ID_KEY] !== config.globalLocationId) {
                resetChartPosition();
            }

            return combinedFilters;
        },
        [chartFilters, updateMap, getDetailLevel, resetChartPosition]
    );

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!state.isMapInitialized) {
                updateMap({});
            }
        }, UPDATE_MAP_ON_MOUNT_TIMEOUT);
        return () => clearTimeout(timeoutId);
    }, [state.isMapInitialized]);

    useEffect(() => {
        resetChartPosition();
    }, [initialTimeUnitValue]);

    if (!entityHierarchies) {
        return null;
    }

    return (
        <>
            <ChartRefinementFiltersControls preprocessFiltersChange={preprocessFiltersChange} />
            {state.echartsMapKey !== 'world' && (
                <Container
                    key={initialTimeUnitValue} // @todo: remove this hack
                    ref={chartRef}
                    echartsMapKey={state.echartsMapKey}
                    mapElementsAmount={state.mapElementsAmount}
                    initialTimeUnitValue={initialTimeUnitValue}
                    conditionsDataTypes={conditionsDataTypes}
                    conditionsDataTypesGranularity={conditionsDataTypesGranularity}
                    isLoadingData={isLoadingChartData}
                    filtersValues={filtersValues}
                    dataResponses={chartDataResponsesByType}
                    conditions={conditions}
                    conditionsPrimaryEntityFilters={conditionsPrimaryEntityFilters}
                    chartFilters={chartFilters}
                    timeUnitKey={timeUnitKey}
                    selectedDataTool={selectedDataTool}
                    selectedDataCollection={selectedDataCollection}
                    onExportData={onExportData}
                    enableExportPNG={enableExportPNG}
                    numberFormatter={numberFormatter}
                    isDengueTool={isDengueTool}
                    theme={theme}
                />
            )}
        </>
    );
};

export default MapChart;
